import type { ApiClient, TicketCounts } from '../core/api';
import { ApiError } from '../core/api';
import type { State } from '../core/state';
import type { ReplayApi, ReplayConfig, ReplayContext, ReplayResult, StopReason } from '../replay/types';
import type { Announcement, Mention, MentionItem, Ticket, TicketMessage, TicketPriority, TicketType } from '../types';
import { loadPano, loadPlayer, loadReplay, loadTour } from '@renderer';
import type { TourApi } from '../tour/index';
import { TOUR_KEY } from '../tour/state';
import { icons } from './icons';
import { fmt, strings, type Strings } from './i18n';
import { selectRegion } from './selector';
import { css } from './styles';

type Tab = 'new' | 'mine' | 'status' | 'pano';

/** Built-in accepted types; the dashboard's list (`uploads.types`, Ayarlar › Sistem › Ek dosyalar) replaces it once the config is in. */
const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  log: 'text/x-log',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
/** The type a file is sent as: a known extension decides (Windows calls a .csv "application/vnd.ms-excel"), else the browser's. */
function mimeOf(f: File, types: Record<string, string>): string {
  return types[f.name.slice(f.name.lastIndexOf('.') + 1).toLowerCase()] ?? f.type;
}
type Att = { blob: Blob; name: string; preview?: string };
/** "Bildirimlerim" page size: small so the tab paints fast; "Daha fazla göster" appends the next page. */
const PAGE_SIZE = 10;
/** Chip order; `all` leads and every other chip appears only when the user actually has that kind of report. */
const CHIP_TYPES: TicketType[] = ['bug', 'suggestion', 'question', 'message', 'dev_request'];
type MinePage = { q: string; type: string; page: number; tickets: Ticket[]; total: number; hasMore: boolean };
type MentionPage = { page: number; items: MentionItem[]; total: number; hasMore: boolean };
/** "@" + up to two words right before the caret: "@meh", "@Mehmet K". */
const MENTION_AT = /(?:^|\s)@([^\s@]{0,30}(?: [^\s@]{0,30})?)$/u;
const MAX_BYTES = 5 * 1024 * 1024;
/** sessionStorage key of a screen recording in this tab (same as replay/recorder STATE_KEY; kept here so the check needs no chunk). */
const REC_KEY = 'fetrace:rec';
function recPhase(): 'rec' | 'done' | null {
  try {
    return (JSON.parse(sessionStorage.getItem(REC_KEY) ?? 'null') as { phase?: 'rec' | 'done' } | null)?.phase ?? null;
  } catch {
    return null;
  }
}
const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string | boolean | undefined> = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === 'html') el.innerHTML = String(v);
    else if (k === 'class') el.className = String(v);
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) el.append(c);
  return el;
}

const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** Shadow DOM launcher + panel (PLAN.md §3.4). Zero dependencies, all styles scoped. */
export class Widget {
  readonly host: HTMLElement;
  private root: ShadowRoot;
  private t: Strings;
  private launcher!: HTMLButtonElement;
  private badge!: HTMLElement;
  private wrap!: HTMLElement;
  private panel!: HTMLElement;
  private body!: HTMLElement;
  private tabButtons = new Map<Tab, HTMLButtonElement>();
  private tab: Tab = 'new';
  private unread = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private openTicket: number | null = null;
  private attachment: Att | null = null;
  /** Reply composer state survives capture/annotate round-trips and back/forward navigation. */
  private replyAttachment: Att | null = null;
  private drafts = new Map<number, string>();
  /** Unsent answers of question blocks, by question id; survive back/forward like reply drafts. */
  private qaDrafts = new Map<number, string>();
  /** Per-ticket "replying to" selection; survives capture round-trips like the draft. */
  private replyTo = new Map<number, { id: number; author: 'user' | 'staff'; mine?: boolean; author_name?: string | null; body: string }>();
  private onReplyToChange: (() => void) | null = null;
  private onMineChip: ((type: string) => void) | null = null;
  private closeMenu: (() => void) | null = null;
  private lastFocus: Element | null = null;
  private band: HTMLElement | null = null;
  /** "Bildirimlerim" search term + last loaded pages: the tab paints from this instantly, then refreshes page 1. */
  private mineQuery = '';
  private mineType = '';
  private mineCache: MinePage | null = null;
  /** Kept outside the page cache so the chips keep their numbers while a new chip's first page loads. */
  private mineCounts: TicketCounts = {};
  private mineChips: HTMLElement | null = null;
  private mineSeq = 0;
  /** "Bildirimlerim" segment: own reports or "Bahsedilenler" (tickets the verified user was "@"-mentioned in). */
  private mineView: 'own' | 'mentions' = 'own';
  private mentionsUnread = 0;
  private mentionsCache: MentionPage | null = null;
  /** Per-ticket "@" picks of the composer: user id → name; sent when "@Name" is still in the text. */
  private mentionPicks = new Map<number, Map<number, string>>();
  /** The stopped screen recording waiting to be sent, and where it belongs (DECISIONS.md §110). */
  private rec: { target: 'new' | number; blob: Blob; ms: number; pages: number; note: string } | null = null;
  /** What the new-report form held when a recording started; put back when the recording returns. */
  private formDraft: Record<string, string> | null = null;

  constructor(
    private readonly state: State,
    private readonly api: ApiClient,
  ) {
    this.t = strings(state.options.locale);
    this.host = h('div', { id: 'fetrace-root' });
    this.root = this.host.attachShadow({ mode: 'open' });
    this.render();
    this.applyTheme();
    (document.body ?? document.documentElement).appendChild(this.host);
    this.renderBand();
  }

  /** Placement priority: dashboard (remote theme.placement) → script/data-attribute option → bottom-left. */
  applyPlacement(): void {
    const p = this.state.remote.theme?.placement ?? {};
    const position = p.position ?? this.state.options.position;
    const cls: Record<string, string> = { 'bottom-right': 'fe-right', 'bottom-center': 'fe-center', 'left-edge': 'fe-edge fe-edge-left', 'right-edge': 'fe-edge fe-edge-right' };
    this.wrap.className = `fe-wrap ${cls[position] ?? 'fe-left'}`;
    this.host.style.setProperty('--fe-ox', `${Math.max(0, p.offset_x ?? 22)}px`);
    this.host.style.setProperty('--fe-oy', `${Math.max(0, p.offset_y ?? 22)}px`);
    this.host.style.setProperty('--fe-edge-top', `${Math.min(90, Math.max(5, p.edge_top ?? 30))}%`);
    this.launcher.hidden = p.launcher === 'hidden' || (p.launcher !== 'button' && !this.state.options.launcher);
    const label = this.launcher.querySelector('.fe-launcher-label');
    if (position.endsWith('-edge') && !label) this.launcher.append(h('span', { class: 'fe-launcher-label' }, [this.t.edgeLabel]));
    else if (!position.endsWith('-edge')) label?.remove();
  }

  /** Dashboard settings that arrived after mount (a /config retry): move/restyle the launcher in place. */
  applyRemote(): void {
    this.applyPlacement();
    this.applyTheme();
  }

  applyTheme(): void {
    const primary = this.state.options.theme?.primary ?? this.state.remote.theme?.primary;
    if (primary) this.host.style.setProperty('--fe-primary', primary);
  }

  /* ---------- shell ---------- */

  private render(): void {
    this.root.innerHTML = '';
    this.root.append(h('style', { html: css }));
    const wrap = h('div', { class: 'fe-wrap' });
    this.wrap = wrap;

    this.badge = h('span', { class: 'fe-badge', hidden: true, 'aria-hidden': 'true' });
    this.launcher = h('button', { class: 'fe-launcher', type: 'button', 'aria-label': this.t.launcher, 'aria-haspopup': 'dialog', 'aria-expanded': 'false', html: icons.bug });
    this.launcher.append(h('span', { class: 'fe-shine', 'aria-hidden': 'true' }), this.badge);
    this.launcher.addEventListener('click', () => this.toggle());
    this.applyPlacement();

    const closeBtn = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.close, html: icons.close });
    closeBtn.addEventListener('click', () => this.close());
    const head = h('div', { class: 'fe-head' }, [
      h('div', {}, [h('h2', { id: 'fe-title' }, [this.t.title]), h('span', { class: 'fe-sub' }, [this.state.remote.project?.name ?? this.t.subtitle])]),
      closeBtn,
    ]);

    const tabs = h('div', { class: 'fe-tabs', role: 'tablist' });
    (['new', 'mine', 'status', 'pano'] as Tab[]).forEach((tab) => {
      const b = h('button', { class: 'fe-tab', type: 'button', role: 'tab', id: `fe-tab-${tab}`, 'aria-selected': tab === this.tab ? 'true' : 'false', 'aria-controls': 'fe-body', hidden: tab === 'pano' }, [this.t.tabs[tab]]);
      b.addEventListener('click', () => this.show(tab));
      this.tabButtons.set(tab, b);
      tabs.append(b);
    });

    this.body = h('div', { class: 'fe-body', id: 'fe-body', role: 'tabpanel' });
    this.panel = h('div', { class: 'fe-panel', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'fe-title', hidden: true }, [head, tabs, this.body]);
    this.panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    wrap.append(this.launcher, this.panel);
    this.root.append(wrap);
    this.wrap = wrap;
  }

  /* ---------- announcement band (PLAN.md §3.4/3, Phase 2 "duyuru bandı") ---------- */

  renderBand(): void {
    this.refreshAttention();
    this.band?.remove();
    this.band = null;
    const dismissed = this.state.dismissedAnnouncements();
    const active = this.state.announcements().filter((a) => !dismissed.includes(a.id));
    const a = active.sort((x, y) => ({ outage: 0, warning: 1, info: 2 })[x.level] - ({ outage: 0, warning: 1, info: 2 })[y.level])[0];
    if (!a || !this.panel.hidden) return;
    const t = this.t.status;
    const close = h('button', { type: 'button', 'aria-label': t.dismiss, html: '&times;' });
    close.addEventListener('click', () => {
      this.state.dismissAnnouncement(a.id);
      this.renderBand();
    });
    this.band = h('div', { class: `fe-band ${a.level}`, role: 'status' }, [h('div', {}, [h('strong', {}, [`${t.band} · ${t.levels[a.level] ?? a.level}`]), a.message]), close]);
    this.wrap.append(this.band);
  }

  open(): void {
    if (!this.panel.hidden) return;
    this.lastFocus = document.activeElement;
    this.panel.hidden = false;
    this.launcher.setAttribute('aria-expanded', 'true');
    this.band?.remove();
    this.band = null;
    this.show(this.tab);
  }

  close(): void {
    this.panel.hidden = true;
    this.launcher.setAttribute('aria-expanded', 'false');
    (this.lastFocus as HTMLElement | null)?.focus?.();
    this.renderBand();
  }

  toggle(): void {
    this.panel.hidden ? this.open() : this.close();
  }

  /** Deep link (e-mail "Konuşmayı aç" button): open the panel straight on one thread. */
  showTicket(id: number): void {
    this.open();
    this.tab = 'mine';
    this.tabButtons.forEach((b, k) => b.setAttribute('aria-selected', k === 'mine' ? 'true' : 'false'));
    void this.renderThread(id);
  }

  destroy(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.host.remove();
  }

  private show(tab: Tab): void {
    this.tab = tab;
    this.tabButtons.forEach((b, k) => b.setAttribute('aria-selected', k === tab ? 'true' : 'false'));
    this.body.innerHTML = '';
    if (tab === 'new') this.renderForm();
    else if (tab === 'mine') void this.renderMine();
    else if (tab === 'pano') this.renderPano();
    else void this.renderStatus();
  }

  /** §245: a customer's yetkili kişi gets "Pano" (the poll says so); pano.js draws it with the panel's requests. */
  private setPano(on: boolean): void {
    this.tabButtons.get('pano')!.hidden = !on;
    if (!on && this.tab === 'pano') this.show('new');
  }

  private renderPano(): void {
    loadPano().then(
      (p) => this.tab === 'pano' && p.render(this.body, { locale: this.state.options.locale ?? 'tr', api: this.api }),
      () => this.body.append(h('p', { class: 'fe-center' }, [this.t.mine.error])),
    );
  }

  /** Thread view pins the composer to the bottom (flex column); every other view is plain flow. */
  private threadLayout(on: boolean): void {
    this.body.classList.toggle('thread', on);
  }

  /* ---------- unread badge ---------- */

  setUnread(n: number, mentions = this.mentionsUnread): void {
    if (n + mentions !== this.unread + this.mentionsUnread) window.dispatchEvent(new CustomEvent('fetrace:unread', { detail: { count: n + mentions } }));
    this.unread = n;
    this.mentionsUnread = mentions;
    this.refreshAttention();
  }

  /**
   * Launcher badge = unread replies + unread "@" mentions + announcements not yet seen; the launcher glows only while
   * that is > 0. Tab badges: "Bildirimlerim" shows replies + mentions; "Durum" shows the active-announcement count for as long as
   * any is live — red while some are unseen, a quiet level-coloured outline once they have been read or dismissed.
   */
  refreshAttention(): void {
    const ann = this.state.unseenAnnouncements().length;
    const total = this.unread + this.mentionsUnread + ann;
    this.badge.textContent = total > 9 ? '9+' : String(total);
    this.badge.hidden = total === 0;
    this.launcher.classList.toggle('fe-attn', total > 0);
    this.tabBadge('mine', this.unread + this.mentionsUnread);
    this.paintSegments();
    const active = this.state.announcements();
    const level = (['outage', 'warning', 'info'] as const).find((l) => active.some((a) => a.level === l)) ?? 'info';
    this.tabBadge('status', active.length, ann > 0 ? '' : `seen ${level}`);
  }

  private tabBadge(tab: Tab, n: number, variant = ''): void {
    const b = this.tabButtons.get(tab);
    if (!b) return;
    let cur = b.querySelector('.fe-tab-badge');
    if (n <= 0) return void cur?.remove();
    const text = n > 9 ? '9+' : String(n);
    if (!cur) b.append((cur = h('span', { class: 'fe-tab-badge' })));
    cur.className = `fe-tab-badge${variant ? ` ${variant}` : ''}`;
    cur.textContent = text;
    cur.setAttribute('aria-label', `(${text})`);
  }

  startPolling(intervalMs: number): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    let n = 0;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      // Announcements: refresh every 5th tick (≈5 min) so a published outage reaches open pages.
      if (n++ % 5 === 4) {
        this.api
          .status()
          .then((r) => {
            this.state.setAnnouncements(r.announcements);
            this.renderBand();
          })
          .catch(() => undefined);
      }
      if (!this.state.identity?.hash && Object.keys(this.state.ticketTokens()).length === 0) return;
      this.api
        .listTickets({ per_page: 1 })
        .then((r) => {
          this.setUnread(r.unread, r.mentions_unread ?? 0);
          this.setPano(!!r.pano);
          // The poll asks for the whole scope, so its counts only describe an unsearched, unfiltered chip row.
          if (this.mineQuery === '' && r.counts) {
            this.mineCounts = r.counts;
            this.paintChips();
          }
        })
        .catch(() => undefined);
    };
    tick();
    this.pollTimer = setInterval(tick, intervalMs);
  }

  /* ---------- tab 1: new report ---------- */

  private renderForm(): void {
    const t = this.t.form;
    const identity = this.state.identity;
    const form = h('form', { novalidate: true, autocomplete: 'off' });
    const alert = h('div', { class: 'fe-alert err', hidden: true, role: 'alert' });

    const field = (label: string, input: HTMLElement, id: string) => {
      input.id = id;
      return h('div', { class: 'fe-field' }, [h('label', { class: 'fe-label', for: id }, [label]), input, h('div', { class: 'fe-error', id: id + '-err' })]);
    };
    const select = (cls: string, options: Record<string, string>, selected: string) => {
      const s = h('select', { class: cls });
      for (const [v, l] of Object.entries(options)) s.append(h('option', { value: v, selected: v === selected }, [l]));
      return s;
    };

    const type = select('fe-select', t.types, 'bug');
    const priority = select('fe-select', t.priorities, 'normal');
    const title = h('input', { class: 'fe-input', type: 'text', maxlength: '255', placeholder: t.titlePlaceholder, required: true });
    const desc = h('textarea', { class: 'fe-textarea', maxlength: '10000', placeholder: t.descriptionPlaceholder, required: true });
    const name = h('input', { class: 'fe-input', type: 'text', maxlength: '255' });
    const email = h('input', { class: 'fe-input', type: 'email', maxlength: '255', required: true });

    const draft = this.formDraft;
    this.formDraft = null;
    if (draft) {
      type.value = draft.type || 'bug';
      priority.value = draft.priority || 'normal';
      title.value = draft.title ?? '';
      desc.value = draft.desc ?? '';
      name.value = draft.name ?? '';
      email.value = draft.email ?? '';
    }

    form.append(alert, h('div', { class: 'fe-row' }, [field(t.type, type, 'fe-type'), field(t.priority, priority, 'fe-priority')]), field(t.title, title, 'fe-title-in'), field(t.description, desc, 'fe-desc'));

    if (identity?.id) {
      form.append(h('p', { class: 'fe-muted' }, [`${t.identified} ${identity.name ?? ''} ${identity.email ? `<${identity.email}>` : ''}`.trim()]));
    } else {
      form.append(h('div', { class: 'fe-row' }, [field(t.name, name, 'fe-name'), field(t.email, email, 'fe-email')]));
    }

    // screenshot
    const preview = h('div', { class: 'fe-preview', hidden: true });
    const captureBtn = h('button', { class: 'fe-btn', type: 'button', html: icons.camera + escape(t.capture) });
    const uploadBtn = h('button', { class: 'fe-btn', type: 'button', html: icons.upload + escape(t.upload) });
    const fileInput = h('input', { type: 'file', class: 'fe-sr', 'aria-label': t.upload });
    const shotErr = h('div', { class: 'fe-error' });
    const renderPreview = () => {
      preview.innerHTML = '';
      preview.hidden = !this.attachment;
      if (!this.attachment) return;
      if (this.attachment.preview) preview.append(h('img', { src: this.attachment.preview, alt: t.screenshot }));
      else preview.append(h('div', { class: 'fe-file' }, [this.attachment.name]));
      const rm = h('button', { class: 'fe-btn fe-remove', type: 'button', 'aria-label': t.remove, html: icons.trash });
      rm.addEventListener('click', () => {
        this.setAttachment(null);
        renderPreview();
      });
      preview.append(rm);
      if (this.attachment.preview && this.attachment.blob.type !== 'application/pdf') {
        const edit = h('button', { class: 'fe-btn fe-edit', type: 'button', style: 'position:absolute;top:6px;left:6px', html: icons.pen + escape(t.edit) });
        edit.addEventListener('click', () => void this.openAnnotator(renderPreview, shotErr, formSlot));
        preview.append(edit);
      }
    };
    const formSlot = { get: () => this.attachment, set: (a: Att | null) => this.setAttachment(a) };
    captureBtn.addEventListener('click', async () => {
      shotErr.textContent = '';
      captureBtn.disabled = true;
      captureBtn.innerHTML = icons.camera + escape(t.capturing);
      try {
        const shot = await this.captureFlow();
        if (shot) {
          this.setAttachment(shot);
          renderPreview();
          await this.openAnnotator(renderPreview, shotErr, formSlot);
        }
      } catch {
        shotErr.textContent = t.captureFailed;
      } finally {
        captureBtn.disabled = false;
        captureBtn.innerHTML = icons.camera + escape(t.capture);
      }
    });
    uploadBtn.addEventListener('click', () => this.pick(fileInput));
    fileInput.addEventListener('change', () => {
      shotErr.textContent = '';
      const f = fileInput.files?.[0];
      fileInput.value = '';
      if (!f) return;
      const att = this.fileToAttachment(f, shotErr);
      if (att) {
        this.setAttachment(att);
        renderPreview();
      }
    });
    renderPreview();
    // Screen recording: the form's content rides along so it is back when the recording stops (maybe on another page).
    const recBtn = this.recOn() ? h('button', { class: 'fe-btn', type: 'button', html: icons.rec + escape(t.rec.start) }) : null;
    recBtn?.addEventListener('click', () => void this.startRec({ target: 'new', draft: { type: type.value, priority: priority.value, title: title.value, desc: desc.value, name: name.value, email: email.value } }, shotErr, recBtn));
    const recSlot = h('div');
    const renderRec = () => {
      recSlot.innerHTML = '';
      if (this.rec?.target === 'new') recSlot.append(this.recRow(renderRec));
      if (recBtn) recBtn.hidden = this.rec?.target === 'new';
    };
    renderRec();
    form.append(h('div', { class: 'fe-field' }, [h('span', { class: 'fe-label' }, [recBtn ? t.rec.section : t.screenshot]), h('div', { class: 'fe-shot' }, [h('div', { class: 'fe-actions' }, [captureBtn, uploadBtn, ...(recBtn ? [recBtn] : []), fileInput]), shotErr, preview, recSlot])]));

    const submit = h('button', { class: 'fe-btn primary', type: 'submit', html: icons.send + escape(t.submit) });
    // §234 tekrar talep: the person's own open ticket on the same topic is offered before a new one goes out, once.
    const dupSlot = h('div');
    let dupAsked = false;
    // At the top, where the form's own alert is: the sticky footer would cover it at the bottom.
    alert.after(dupSlot);
    form.append(h('div', { class: 'fe-foot' }, [h('span', { class: 'fe-brand' }, [this.t.poweredBy]), submit]));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      alert.hidden = true;
      let ok = true;
      const check = (input: HTMLInputElement | HTMLTextAreaElement, id: string, valid: boolean, msg: string) => {
        const err = form.querySelector('#' + id + '-err')!;
        err.textContent = valid ? '' : msg;
        input.setAttribute('aria-invalid', valid ? 'false' : 'true');
        ok = ok && valid;
      };
      check(title, 'fe-title-in', title.value.trim() !== '', t.required);
      check(desc, 'fe-desc', desc.value.trim() !== '', t.required);
      if (!identity?.id) check(email, 'fe-email', /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()), t.invalidEmail);
      if (!ok) return;

      submit.disabled = true;
      submit.innerHTML = icons.send + escape(t.sending);
      // Only a browser that has tickets of its own (tokens, or a verified user) has anything to be offered.
      if (!dupAsked && (Object.keys(this.state.ticketTokens()).length > 0 || !!identity?.hash)) {
        dupAsked = true;
        // Asking never stands in the way: no answer, no offer.
        const found = await this.api.similar(title.value.trim(), desc.value.trim()).then(
          (r) => r.ticket,
          () => null,
        );
        if (found) {
          submit.disabled = false;
          submit.innerHTML = icons.send + escape(t.submit);
          const add = h('button', { class: 'fe-btn primary', type: 'button' }, [t.duplicate.add]);
          const separate = h('button', { class: 'fe-btn', type: 'button' }, [t.duplicate.separate]);
          separate.addEventListener('click', () => {
            dupSlot.innerHTML = '';
            form.requestSubmit();
          });
          add.addEventListener('click', async () => {
            add.disabled = separate.disabled = submit.disabled = true;
            add.textContent = t.duplicate.adding;
            alert.hidden = true;
            try {
              const tokens: string[] = [];
              if (this.attachment) tokens.push(await this.uploadAttachment(this.attachment));
              const rec = this.rec?.target === 'new' ? this.rec : null;
              if (rec) tokens.push(await this.api.upload(rec.blob, 'ekran-kaydi.json.gz', 'replay'));
              await this.api.reply(found.id, `${title.value.trim()}\n\n${desc.value.trim()}`, tokens);
              this.setAttachment(null);
              if (rec) this.dropRec();
              this.showTicket(found.id);
            } catch {
              alert.textContent = t.error;
              alert.hidden = false;
              add.disabled = separate.disabled = submit.disabled = false;
              add.textContent = t.duplicate.add;
            }
          });
          dupSlot.replaceChildren(
            h('div', { class: 'fe-alert', role: 'status' }, [
              h('p', { style: 'margin:0 0 8px' }, [fmt(t.duplicate.text, { ref: found.reference, title: found.title })]),
              h('div', { class: 'fe-actions' }, [add, separate]),
            ]),
          );
          // Back to the top, where the offer is; the panel itself must not stay shifted by the inserted block.
          this.body.scrollTop = 0;
          this.panel.scrollTop = 0;
          return;
        }
      }
      try {
        let screenshotToken: string | undefined;
        let attachmentTokens: string[] = [];
        if (this.attachment) {
          const token = await this.uploadAttachment(this.attachment);
          if (this.attachment.blob.type.startsWith('image/')) screenshotToken = token;
          else attachmentTokens = [token];
        }
        const rec = this.rec?.target === 'new' ? this.rec : null;
        if (rec) attachmentTokens.push(await this.api.upload(rec.blob, 'ekran-kaydi.json.gz', 'replay'));
        const { ticket } = await this.api.createTicket({
          title: title.value.trim(),
          description: desc.value.trim(),
          type: type.value as TicketType,
          priority: priority.value as TicketPriority,
          contact: identity?.id ? undefined : { name: name.value.trim(), email: email.value.trim() },
          snapshot: this.state.snapshot(),
          screenshot_token: screenshotToken,
          attachment_tokens: attachmentTokens,
        });
        if (ticket.ticket_token) this.state.rememberTicket(ticket.id, ticket.ticket_token);
        this.setAttachment(null);
        if (rec) this.dropRec();
        this.renderSuccess(ticket);
      } catch (err) {
        const api = err instanceof ApiError ? err : null;
        // 403 `ticket_limit`: the customer's daily / weekly / monthly quota is used up (DECISIONS #105).
        alert.textContent = (api?.body as { error?: string } | null)?.error === 'ticket_limit' ? t.limitReached : api?.status === 429 ? t.rateLimited : t.error;
        alert.hidden = false;
        submit.disabled = false;
        submit.innerHTML = icons.send + escape(t.submit);
      }
    });

    this.body.append(form);
    title.focus();
  }

  /**
   * Phase 2 drawing tool: red box / arrow / blur over the captured image. Opens full screen over the
   * page (the panel is hidden meanwhile); Tamam replaces the slot's attachment, Vazgeç/Esc keeps it.
   */
  private async openAnnotator(onDone: () => void, err: HTMLElement, slot: { get: () => Att | null; set: (a: Att | null) => void }): Promise<void> {
    const current = slot.get();
    if (!current?.preview) return;
    const t = this.t.form.annotate;
    let renderer: Awaited<ReturnType<typeof import('./screenshot').renderer>>;
    let img: HTMLImageElement;
    try {
      renderer = await (await import('./screenshot')).renderer();
      img = await renderer.loadImage(current.preview);
    } catch {
      err.textContent = this.t.form.captureFailed;
      return;
    }
    const overlay = h('div', { class: 'fe-annot', role: 'dialog', 'aria-modal': 'true', 'aria-label': t.title });
    const tools = h('div', { class: 'fe-annot-tools' });
    const stage = h('div', { class: 'fe-annot-stage' });
    const canvas = h('canvas');
    stage.append(canvas);
    const annot = new renderer.Annotator(img, canvas);
    // Fit the capture to the stage (small regions enlarge up to 2× their on-page size); pointer mapping follows the CSS box.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const k = Math.min(2, (stage.clientWidth - 24) / (canvas.width / dpr), (stage.clientHeight - 24) / (canvas.height / dpr));
      canvas.style.width = Math.max(1, Math.floor((canvas.width / dpr) * k)) + 'px';
      canvas.style.height = Math.max(1, Math.floor((canvas.height / dpr) * k)) + 'px';
    };
    const toolButtons: HTMLButtonElement[] = [];
    (['rect', 'arrow', 'blur'] as const).forEach((tool) => {
      const b = h('button', { class: 'fe-btn', type: 'button', 'aria-pressed': tool === 'rect' ? 'true' : 'false' }, [t[tool]]);
      b.addEventListener('click', () => {
        annot.tool = tool;
        toolButtons.forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
      });
      toolButtons.push(b);
      tools.append(b);
    });
    const undo = h('button', { class: 'fe-btn', type: 'button' }, [t.undo]);
    undo.addEventListener('click', () => annot.undo());
    const clear = h('button', { class: 'fe-btn', type: 'button' }, [t.clear]);
    clear.addEventListener('click', () => annot.clear());
    const cancel = h('button', { class: 'fe-btn', type: 'button', style: 'margin-left:auto' }, [t.cancel]);
    const done = h('button', { class: 'fe-btn primary', type: 'button' }, [t.done]);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeOverlay();
      } else if (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        annot.undo();
      }
    };
    const closeOverlay = () => {
      annot.destroy();
      overlay.remove();
      window.removeEventListener('resize', fit);
      document.removeEventListener('keydown', onKey, true);
      this.panel.hidden = false;
    };
    cancel.addEventListener('click', closeOverlay);
    done.addEventListener('click', async () => {
      try {
        const blob = await annot.toBlob('image/png');
        slot.set({ blob, name: 'screenshot.png', preview: URL.createObjectURL(blob) });
        onDone();
      } catch {
        err.textContent = this.t.form.captureFailed;
      }
      closeOverlay();
    });
    tools.append(undo, clear, cancel, done);
    overlay.append(h('div', { class: 'fe-annot-bar' }, [tools, h('p', { class: 'fe-muted' }, [t.hint])]), stage);
    this.panel.hidden = true;
    this.band?.remove();
    this.wrap.append(overlay);
    fit();
    window.addEventListener('resize', fit);
    document.addEventListener('keydown', onKey, true);
    done.focus();
  }

  private setAttachment(a: Att | null): void {
    if (this.attachment?.preview) URL.revokeObjectURL(this.attachment.preview);
    this.attachment = a;
  }

  private setReplyAttachment(a: Att | null): void {
    if (this.replyAttachment?.preview) URL.revokeObjectURL(this.replyAttachment.preview);
    this.replyAttachment = a;
  }

  private types(): Record<string, string> {
    return this.state.remote.uploads?.types ?? MIME_BY_EXT;
  }

  /** Open the file picker filtered to the accepted types (MIME types plus extensions, for pickers without a MIME map). */
  private pick(input: HTMLInputElement): void {
    const types = this.types();
    input.accept = Object.values(types).concat(Object.keys(types).map((e) => '.' + e)) + ''; // an array's string form is its comma list
    input.click();
  }

  /** Validate a picked/pasted/dropped file. Images are normalised (size cap, lossless where possible) at upload time. */
  private fileToAttachment(f: File, err: HTMLElement): Att | null {
    const t = this.t.form;
    if (f.size > MAX_BYTES) {
      err.textContent = t.fileTooLarge;
      return null;
    }
    const types = this.types();
    const mime = mimeOf(f, types);
    if (!Object.values(types).includes(mime)) {
      err.textContent = t.fileType + ' ' + Object.keys(types).join(', ');
      return null;
    }
    // Re-wrap files the browser typed differently (or not at all) so presign/PUT carry the right Content-Type.
    const blob: Blob = f.type === mime ? f : new File([f], f.name, { type: mime });
    return { blob, name: f.name, preview: mime.startsWith('image/') ? URL.createObjectURL(f) : undefined };
  }

  /** Hide the panel, let the user pick a region on the live page, render it. Returns null when cancelled. */
  private async captureFlow(): Promise<Att | null> {
    this.panel.hidden = true;
    this.band?.remove();
    let region: Awaited<ReturnType<typeof selectRegion>>;
    try {
      region = await selectRegion(this.root, this.t.form.select);
    } finally {
      this.panel.hidden = false;
    }
    if (region === null) return null;
    const { captureScreen } = await import('./screenshot');
    const blob = await captureScreen(this.host, region);
    return { blob, name: 'screenshot.png', preview: URL.createObjectURL(blob) };
  }

  /** Images are capped at 2200 px on the long edge with high-quality resampling; untouched when already within limits. */
  private async uploadAttachment(a: Att): Promise<string> {
    let blob = a.blob;
    if (blob.type.startsWith('image/')) {
      const { normalizeImage } = await import('./screenshot');
      blob = await normalizeImage(blob, 2200, MAX_BYTES);
    }
    return this.api.upload(blob, a.name);
  }

  /* ---------- screen recording (docs/FAZ3-PLAN.md A2, DECISIONS.md §110) ---------- */

  private recOn(): boolean {
    return !!this.state.remote.replay?.enabled;
  }

  private recConfig(): ReplayConfig {
    const r = this.state.remote.replay;
    const t = this.t.form.rec;
    return {
      maxSeconds: r?.max_seconds ?? 300,
      maxBytes: r?.max_bytes ?? MAX_BYTES,
      maskSelector: r?.mask_selector,
      blockSelector: r?.block_selector,
      redactFields: this.state.remote.redact?.fields,
      labels: { recording: t.recording, stop: t.stop, cancel: t.cancel },
    };
  }

  private async replay(): Promise<ReplayApi> {
    const r = await loadReplay();
    r.onStop((res, why, ctx) => this.onRecStop(res, why, ctx));
    return r;
  }

  /** On mount: a recording that was running when the last page unloaded keeps going; a stopped, unsent one is put back. */
  async resumeReplay(): Promise<void> {
    const phase = recPhase();
    if (!phase || !this.recOn()) return;
    if (phase === 'rec') this.launcher.hidden = true;
    try {
      const r = await this.replay();
      await r.resume(this.recConfig());
      const done = await r.finished();
      if (done && !this.rec) this.takeRec(done, 'stop', false);
    } catch {
      this.applyPlacement();
    }
  }

  /** The panel closes so the page can be used; the launcher hides until the recording stops. */
  private async startRec(ctx: ReplayContext, err: HTMLElement, btn?: HTMLButtonElement | null): Promise<void> {
    const t = this.t.form.rec;
    err.textContent = '';
    const label = btn?.innerHTML ?? '';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = icons.rec + escape(t.starting);
    }
    try {
      const r = await this.replay();
      this.rec = null;
      this.close();
      this.launcher.hidden = true;
      await r.start(this.recConfig(), ctx);
    } catch {
      this.applyPlacement();
      this.open();
      err.textContent = t.failed;
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = label;
      }
    }
  }

  /** Stopped, cancelled or too big: either way the panel comes back where the recording started, with what was typed. */
  private onRecStop(res: ReplayResult | null, why: StopReason, ctx: ReplayContext | null): void {
    this.applyPlacement();
    if (res) return this.takeRec(res, why, true);
    if (ctx) this.restore(ctx, true);
    else this.open();
    if (why === 'limit') {
      const alert = this.body.querySelector('.fe-alert, .fe-reply-wrap .fe-error') as HTMLElement | null;
      if (alert) {
        alert.textContent = this.t.form.rec.tooBig;
        alert.hidden = false;
      }
    }
  }

  /** Attach a stopped recording to its form or reply box, restore what was typed there and (when `show`) open it. */
  private takeRec(res: ReplayResult, why: StopReason, show: boolean): void {
    this.rec = { target: res.ctx.target, blob: res.blob, ms: res.durationMs, pages: res.pages, note: why === 'limit' ? this.t.form.rec.limit : '' };
    this.restore(res.ctx, show);
  }

  private restore({ target, draft }: ReplayContext, show: boolean): void {
    if (target === 'new') this.formDraft = draft ?? null;
    else if (draft?.body) this.drafts.set(target, draft.body);
    if (!show) return;
    if (target !== 'new') return this.showTicket(target);
    this.tab = 'new';
    if (this.panel.hidden) this.open();
    else this.show('new');
    this.body.querySelector('.fe-rec')?.scrollIntoView?.({ block: 'nearest' });
  }

  private dropRec(): void {
    this.rec = null;
    void loadReplay()
      .then((r) => r.discard())
      .catch(() => undefined);
  }

  /** A recording in the thread: "● Ekran kaydı · 0:48 · İzle" opens the player full screen; `att` null = expired, label only. */
  private recChip(id: number, att: number | null, ms: number): HTMLElement {
    const r = this.t.form.rec;
    if (att === null) return h('span', { class: 'fe-att-file fe-rec', html: icons.rec + escape(r.expired) });
    const b = h('button', { class: 'fe-att-file fe-rec', type: 'button', html: icons.rec + escape(`${r.label} · ${clock(ms)} · ${r.watch}`) });
    b.addEventListener('click', async () => {
      b.disabled = true;
      try {
        const [p, data] = await Promise.all([loadPlayer(), this.api.recording(id, att)]);
        await p.open(this.root, data, this.state.options.locale, () => b.focus());
      } catch {
        b.innerHTML = icons.rec + escape(r.watchFailed);
      }
      b.disabled = false;
    });
    return b;
  }

  /** "● Ekran kaydı · 0:48 · 2 sayfa · 1.2 MB" with a remove button. */
  private recRow(onRemove: () => void): HTMLElement {
    const r = this.rec!;
    const t = this.t.form.rec;
    const rm = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.form.remove, title: this.t.form.remove, html: icons.close });
    rm.addEventListener('click', () => {
      this.dropRec();
      onRemove();
    });
    const info = [fmt(t.pages, { n: r.pages }), this.fileSize(r.blob.size), r.note].filter(Boolean).join(' · ');
    return h('div', { class: 'fe-reply-att fe-rec', role: 'status' }, [h('span', { html: icons.rec }), h('span', { class: 'fe-att-name' }, [`${t.label} · ${clock(r.ms)} `, h('small', {}, [info])]), rm]);
  }

  /** Scroll the quoted message into view and highlight it briefly. */
  private flashMessage(mid: number): void {
    const el = this.body.querySelector(`.fe-msg[data-mid="${mid}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('fe-flash');
    setTimeout(() => el.classList.remove('fe-flash'), 1200);
  }

  private fileSize(n?: number | null): string {
    if (!n) return '';
    return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
  }

  private renderSuccess(ticket: Ticket): void {
    const t = this.t.form;
    this.body.innerHTML = '';
    const again = h('button', { class: 'fe-btn', type: 'button' }, [t.another]);
    again.addEventListener('click', () => this.show('new'));
    const mine = h('button', { class: 'fe-btn primary', type: 'button' }, [t.viewMine]);
    mine.addEventListener('click', () => this.show('mine'));
    // "Nasıl yapılır" (§213): a guide matched the question; it comes first, the reference stays below.
    if (ticket.guide?.status === 'offered') {
      this.body.append(this.guideCard(ticket, true));
    }
    this.body.append(
      h('div', { class: 'fe-center' }, [
        h('div', { class: 'fe-big', html: icons.check }),
        h('p', { style: 'font-weight:600;color:var(--fe-fg);margin:0 0 4px' }, [t.success]),
        h('p', {}, [fmt(t.successRef, { ref: ticket.reference })]),
        h('div', { class: 'fe-actions', style: 'justify-content:center;margin-top:12px' }, [again, mine]),
      ]),
    );
  }

  /* ---------- "Nasıl yapılır" guides (DECISIONS.md §213) ---------- */

  /** tour.js does the guide work; the panel only lends it these hooks. */
  private tour(): Promise<TourApi> {
    return loadTour().then((tour) => {
      tour.attach({
        locale: this.state.options.locale ?? 'tr',
        primary: this.state.options.theme?.primary ?? this.state.remote.theme?.primary,
        position: this.state.remote.theme?.placement?.position ?? this.state.options.position,
        icon: icons.check,
        report: (id, event, step) => this.api.guideEvent(id, event, step),
        close: () => this.close(),
        openTicket: (id) => this.showTicket(id),
      });
      return tour;
    });
  }

  /** The guide offered on a ticket; the card comes with tour.js, the panel only holds its place. */
  private guideCard(ticket: Ticket, declinable = false): HTMLElement {
    const slot = h('div');
    this.tour()
      .then((tour) => slot.replaceWith(tour.offerCard(ticket.id, ticket.guide!, declinable)))
      .catch(() => slot.remove());
    return slot;
  }

  /** On mount: a guide that was running when the last page unloaded carries on here. */
  async resumeGuide(): Promise<void> {
    try {
      if (localStorage.getItem(TOUR_KEY)) (await this.tour()).resume();
    } catch {
      /* storage blocked or tour.js unreachable: the guide waits for the next page */
    }
  }

  /* ---------- tab 2: my reports ---------- */

  private async renderMine(): Promise<void> {
    const t = this.t.mine;
    this.openTicket = null;
    this.closeMenu?.();
    this.threadLayout(false);
    this.body.innerHTML = '';
    // Only a verified identity can be mentioned, so only they get the "Bildirimlerim | Bahsedilenler" switch.
    if (this.state.identity?.hash) this.body.append(this.segments());
    else this.mineView = 'own';
    if (this.mineView === 'mentions') return this.renderMentions();

    const input = h('input', { class: 'fe-input', type: 'search', placeholder: t.search, 'aria-label': t.search, autocomplete: 'off', enterkeyhint: 'search', value: this.mineQuery });
    const list = h('div', { class: 'fe-mine' });
    this.mineChips = h('div', { class: 'fe-chips', role: 'group', 'aria-label': t.types.all });
    this.body.append(this.mineChips, h('div', { class: 'fe-search' }, [h('span', { class: 'fe-search-ico', 'aria-hidden': 'true', html: icons.search }), input]), list);
    this.paintChips();

    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      const q = input.value.trim();
      if (q === this.mineQuery && this.mineCache?.q === q) return;
      this.mineQuery = q;
      if (!list.querySelector('.fe-list')) list.replaceChildren(h('p', { class: 'fe-center' }, [t.loading]));
      void this.loadMine(list, 1);
    };
    this.onMineChip = (type: string) => {
      if (type === this.mineType) return;
      this.mineType = type;
      this.paintChips();
      list.replaceChildren(h('p', { class: 'fe-center' }, [t.loading]));
      void this.loadMine(list, 1);
    };
    input.addEventListener('input', () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, 250);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      run();
    });

    // Paint the last result immediately (fast open, back from a thread); a fresh page 1 replaces it when it lands.
    if (this.mineCache && this.mineCache.q === this.mineQuery && this.mineCache.type === this.mineType) this.paintMine(list, this.mineCache);
    else list.append(h('p', { class: 'fe-center' }, [t.loading]));
    await this.loadMine(list, 1);
  }

  /** "Bildirimlerim | Bahsedilenler" switch; each side carries its own unread badge. */
  private segments(): HTMLElement {
    const t = this.t.mine;
    const seg = h('div', { class: 'fe-seg', role: 'group' });
    (['own', 'mentions'] as const).forEach((view) => {
      const b = h('button', { class: 'fe-seg-btn', type: 'button', 'data-view': view, 'aria-pressed': view === this.mineView ? 'true' : 'false' }, [t.views[view]]);
      b.addEventListener('click', () => {
        if (view === this.mineView) return;
        this.mineView = view;
        void this.renderMine();
      });
      seg.append(b);
    });
    queueMicrotask(() => this.paintSegments());
    return seg;
  }

  private paintSegments(): void {
    this.body?.querySelectorAll<HTMLElement>('.fe-seg-btn').forEach((b) => {
      const n = b.dataset.view === 'mentions' ? this.mentionsUnread : this.unread;
      const cur = b.querySelector('.fe-chip-badge');
      if (n <= 0) return void cur?.remove();
      if (cur) cur.textContent = String(n);
      else b.append(h('span', { class: 'fe-chip-badge' }, [String(n)]));
    });
  }

  /** "Bahsedilenler": one card per ticket the user was mentioned in; paints from cache, then refreshes page 1. */
  private async renderMentions(): Promise<void> {
    const t = this.t.mine;
    const list = h('div', { class: 'fe-mine' });
    this.body.append(list);
    if (this.mentionsCache) this.paintMentions(list, this.mentionsCache);
    else list.append(h('p', { class: 'fe-center' }, [t.loading]));
    await this.loadMentions(list, 1);
  }

  private async loadMentions(list: HTMLElement, page: number): Promise<void> {
    const seq = ++this.mineSeq;
    try {
      const r = await this.api.mentions(page, PAGE_SIZE);
      this.setUnread(this.unread, r.unread);
      if (seq !== this.mineSeq || this.tab !== 'mine' || this.openTicket !== null || this.mineView !== 'mentions') return;
      const items = [...(page > 1 && this.mentionsCache ? this.mentionsCache.items : []), ...r.items];
      this.mentionsCache = { page, items, total: r.total, hasMore: r.has_more };
      this.paintMentions(list, this.mentionsCache);
    } catch {
      if (seq === this.mineSeq) list.replaceChildren(h('p', { class: 'fe-center' }, [this.t.mine.error]));
    }
  }

  private paintMentions(list: HTMLElement, cache: MentionPage): void {
    const t = this.t.mine;
    list.innerHTML = '';
    if (!cache.items.length) return void list.append(h('p', { class: 'fe-center' }, [t.mentionsEmpty]));
    const ul = h('ul', { class: 'fe-list' });
    for (const m of cache.items) {
      const ticket = m.ticket;
      const open = h('button', { class: 'fe-item-main', type: 'button' }, [
        h('div', { class: 'fe-item-title' }, [...(m.unread ? [h('span', { class: 'fe-dot', 'aria-label': '•' })] : []), ticket.title]),
        h('div', { class: 'fe-item-mention' }, [h('strong', {}, [fmt(t.mentionedYou, { name: m.by_staff ? t.staff : m.by })]), m.excerpt ? ` · ${m.excerpt}` : '']),
      ]);
      open.addEventListener('click', () => void this.renderThread(ticket.id));
      ul.append(
        h('li', {}, [
          h('div', { class: `fe-item st-${ticket.status}` }, [
            h('div', { class: 'fe-item-top' }, [h('span', {}, [`${ticket.reference} · ${this.date(m.at)}`]), h('span', { class: `fe-status ${ticket.status}` }, [t.status[ticket.status] ?? ticket.status])]),
            open,
          ]),
        ]),
      );
    }
    list.append(ul);
    if (cache.hasMore) {
      const more = h('button', { class: 'fe-btn fe-more', type: 'button' }, [t.loadMore]);
      more.addEventListener('click', () => {
        more.disabled = true;
        more.textContent = t.loading;
        void this.loadMentions(list, cache.page + 1);
      });
      list.append(h('div', { class: 'fe-more-row' }, [more]));
    }
  }

  /** Side of a bubble: the viewer's own messages right ("Siz"), other end users and staff left. */
  private side(m: { author: 'user' | 'staff'; mine?: boolean }): 'user' | 'other' | 'staff' {
    return m.author === 'staff' ? 'staff' : m.mine === false ? 'other' : 'user';
  }

  private who(m: { author: 'user' | 'staff'; mine?: boolean; author_name?: string | null }): string {
    const side = this.side(m);
    return side === 'user' ? this.t.mine.you : side === 'staff' ? this.t.mine.staff : m.author_name || this.t.mine.user;
  }

  /** Message text with every "@Name" of a mentioned user highlighted; plain text nodes only (no HTML). */
  private bodyNodes(m: TicketMessage): (Node | string)[] {
    const names = (m.mentions ?? []).map((x) => x.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!names.length) return [m.body];
    const re = new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}))`, 'giu');
    return m.body.split(re).map((part, i) => (i % 2 ? h('span', { class: 'fe-mention' }, [part]) : part));
  }

  /**
   * Type chips with their unread badges. Only the kinds the user actually has get a chip, and the row
   * disappears entirely when there is nothing to split — one kind of report needs no filter.
   */
  private paintChips(): void {
    const chips = this.mineChips;
    if (!chips) return;
    const t = this.t.mine;
    const present = CHIP_TYPES.filter((type) => (this.mineCounts[type]?.total ?? 0) > 0 || type === this.mineType);
    chips.hidden = present.length < 2;
    if (chips.hidden) return void chips.replaceChildren();
    chips.replaceChildren(
      ...(['all', ...present] as (TicketType | 'all')[]).map((type) => {
        const key = type === 'all' ? '' : type;
        const unread = this.mineCounts[type]?.unread ?? 0;
        const chip = h('button', { class: 'fe-chip', type: 'button', 'aria-pressed': key === this.mineType ? 'true' : 'false' }, [
          t.types[type] ?? type,
          ...(unread ? [h('span', { class: 'fe-chip-badge', 'aria-label': fmt(t.unreadCount, { n: String(unread) }) }, [String(unread)])] : []),
        ]);
        chip.addEventListener('click', () => this.onMineChip?.(key));
        return chip;
      }),
    );
  }

  /** Fetch one page; page > 1 appends to the cached pages of the same query and chip. Stale responses are dropped. */
  private async loadMine(list: HTMLElement, page: number): Promise<void> {
    const t = this.t.mine;
    const q = this.mineQuery;
    const type = this.mineType;
    const seq = ++this.mineSeq;
    try {
      const r = await this.api.listTickets({ q, type, page, per_page: PAGE_SIZE });
      this.setUnread(r.unread, r.mentions_unread ?? 0);
      if (seq !== this.mineSeq || this.tab !== 'mine' || this.openTicket !== null) return;
      const same = this.mineCache?.q === q && this.mineCache?.type === type;
      const prev = page > 1 && same ? this.mineCache!.tickets : [];
      const tickets = [...prev, ...r.tickets];
      this.mineCache = { q, type, page, tickets, total: r.total ?? tickets.length, hasMore: r.has_more ?? false };
      this.mineCounts = r.counts ?? {};
      this.paintChips();
      this.paintMine(list, this.mineCache);
    } catch {
      if (seq !== this.mineSeq) return;
      list.replaceChildren(h('p', { class: 'fe-center' }, [t.error]));
    }
  }

  private paintMine(list: HTMLElement, cache: MinePage): void {
    const t = this.t.mine;
    list.innerHTML = '';
    if (!cache.tickets.length) return void list.append(h('p', { class: 'fe-center' }, [cache.q ? t.noResults : t.empty]));
    const ul = h('ul', { class: 'fe-list' });
    for (const ticket of cache.tickets) ul.append(h('li', {}, [this.ticketItem(ticket, list)]));
    list.append(ul);
    const shown = fmt(t.showing, { shown: String(cache.tickets.length), total: String(cache.total) });
    if (cache.hasMore) {
      const more = h('button', { class: 'fe-btn fe-more', type: 'button' }, [t.loadMore, h('span', { class: 'fe-muted' }, [shown])]);
      more.addEventListener('click', () => {
        more.disabled = true;
        more.textContent = t.loading;
        void this.loadMine(list, cache.page + 1);
      });
      list.append(h('div', { class: 'fe-more-row' }, [more]));
    } else if (cache.total > PAGE_SIZE) {
      list.append(h('div', { class: 'fe-more-row' }, [h('span', { class: 'fe-muted' }, [shown])]));
    }
  }

  /**
   * A card is a <div>, not a <button>: the pin toggle is a real button and buttons must not nest.
   * Everything below the meta row opens the thread, which is the whole readable area of the card.
   */
  private ticketItem(ticket: Ticket, list: HTMLElement): HTMLElement {
    const t = this.t.mine;
    const open = h('button', { class: 'fe-item-main', type: 'button' }, [
      h('div', { class: 'fe-item-title' }, [...(ticket.unread ? [h('span', { class: 'fe-dot', 'aria-label': '•' })] : []), ticket.title]),
      ...(this.plannedText(ticket) && ticket.status !== 'closed' ? [h('div', { class: 'fe-item-plan' }, [h('span', { 'aria-hidden': 'true', html: icons.clock }), `${t.planned}: ${this.plannedText(ticket)}`])] : []),
      ...(ticket.open_questions ? [h('div', { class: 'fe-item-qa', html: icons.ask + escape(this.t.q.asked) })] : []),
    ]);
    open.addEventListener('click', () => void this.renderThread(ticket.id));

    const label = ticket.pinned ? t.unpin : t.pin;
    const pin = h('button', { class: 'fe-pin', type: 'button', 'aria-pressed': ticket.pinned ? 'true' : 'false', 'aria-label': label, title: label, html: icons.pin });
    pin.addEventListener('click', () => void this.togglePin(ticket, pin, list));

    return h('div', { class: `fe-item st-${ticket.status}${ticket.pinned ? ' fe-pinned' : ''}` }, [
      h('div', { class: 'fe-item-top' }, [
        h('span', {}, [`${ticket.reference} · ${this.date(ticket.last_message_at ?? ticket.created_at)}`]),
        h('span', { class: `fe-status ${ticket.status}` }, [t.status[ticket.status] ?? ticket.status]),
        pin,
      ]),
      open,
    ]);
  }

  /** Pinning reorders the whole scope server-side, so the list is reloaded from page 1 rather than patched. */
  private async togglePin(ticket: Ticket, btn: HTMLButtonElement, list: HTMLElement): Promise<void> {
    btn.disabled = true;
    try {
      await this.api.pinTicket(ticket.id, !ticket.pinned);
      ticket.pinned = !ticket.pinned;
      await this.loadMine(list, 1);
    } catch {
      btn.disabled = false;
      list.prepend(h('p', { class: 'fe-error', role: 'alert' }, [this.t.mine.pinFailed]));
    }
  }

  private async renderThread(id: number): Promise<void> {
    const t = this.t.mine;
    this.openTicket = id;
    this.body.innerHTML = '';
    this.body.append(h('p', { class: 'fe-center' }, [t.loading]));
    try {
      const { ticket } = await this.api.ticket(id);
      if (this.openTicket !== id) return;
      this.body.innerHTML = '';
      this.threadLayout(true);
      const back = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.back, html: icons.back });
      back.addEventListener('click', () => void this.renderMine());
      this.body.append(
        h('div', { class: `fe-thread-head st-${ticket.status}` }, [back, h('div', {}, [h('div', { class: 'fe-t' }, [ticket.title]), h('div', { class: 'fe-muted' }, [`${ticket.reference} · `, h('span', { class: `fe-status ${ticket.status}` }, [t.status[ticket.status] ?? ticket.status])])])]),
      );
      if (ticket.guide) this.body.append(this.guideCard(ticket));
      const planned = this.plannedText(ticket);
      if (planned && ticket.status !== 'closed') {
        this.body.append(h('div', { class: 'fe-plan', role: 'status' }, [h('span', { class: 'fe-sys-ico', 'aria-hidden': 'true', html: icons.clock }), h('div', {}, [h('strong', {}, [t.planned]), planned])]));
      }
      const thread = h('div', { class: 'fe-thread' });
      const recordings = [...(ticket.recordings ?? [])];
      for (const m of ticket.messages ?? []) {
        if (m.kind === 'questions' && m.questions) {
          thread.append(this.questionCard(id, ticket, m));
          continue;
        }
        const label = this.who(m);
        const side = this.side(m);
        const bubble = h('div', { class: `fe-msg ${side}`, 'data-mid': String(m.id) });
        if (m.reply_to) {
          const q = m.reply_to;
          const quote = h('button', { class: 'fe-quote', type: 'button', title: t.jumpToQuoted }, [h('span', { class: 'fe-quote-author' }, [this.who(q)]), h('span', { class: 'fe-quote-body' }, [q.body])]);
          quote.addEventListener('click', () => this.flashMessage(q.id));
          bubble.append(quote);
        }
        if (m.kind === 'answers' && m.answers) bubble.append(h('span', { class: 'fe-qa-done', html: icons.check + escape(`${this.t.q.answered} · ${fmt(this.t.q.count, { n: m.answers.items.length })}`) }));
        else if (m.body) bubble.append(...this.bodyNodes(m));
        for (const a of m.attachments ?? []) {
          if (a.kind === 'replay') {
            bubble.append(this.recChip(id, a.expired ? null : (a.id ?? null), a.duration_ms ?? 0));
            continue;
          }
          if (!a.url) continue;
          if (a.mime?.startsWith('image/')) {
            const img = h('img', { src: a.url, alt: a.name ?? '', loading: 'lazy' });
            // images arrive after the initial scroll-to-bottom; keep the newest message in view
            img.addEventListener('load', () => {
              if (this.openTicket === id) this.body.scrollTop = this.body.scrollHeight;
            });
            bubble.append(h('a', { class: 'fe-att-img', href: a.url, target: '_blank', rel: 'noreferrer' }, [img]));
          }
          else bubble.append(h('a', { class: 'fe-att-file', href: a.url, target: '_blank', rel: 'noreferrer', html: icons.clip + escape(a.name ?? 'ek') + (a.size ? ` <small>· ${this.fileSize(a.size)}</small>` : '') }));
        }
        // Recordings sent with the report itself ride on the report's first message.
        if (m.author === 'user' && recordings.length) recordings.splice(0).forEach((r) => bubble.append(this.recChip(id, r.id, r.duration_ms)));
        bubble.append(h('span', { class: 'fe-meta' }, [`${label} · ${this.date(m.created_at)}${m.edited_at ? ` · ${t.edited}` : ''}`]));
        const row = h('div', { class: `fe-msg-row ${side}` }, [h('div', { class: 'fe-avatar', 'aria-hidden': 'true' }, [label.slice(0, 1).toUpperCase()]), bubble]);
        if (ticket.status !== 'closed') {
          const reply = h('button', { class: 'fe-msg-act', type: 'button', 'aria-label': t.replyTo, title: t.replyTo, html: icons.reply });
          reply.addEventListener('click', () => {
            this.replyTo.set(id, { id: m.id, author: m.author, mine: m.mine, author_name: m.author_name, body: m.body || (m.attachments ?? []).map((a) => a.name).filter(Boolean).join(', ') });
            this.onReplyToChange?.();
            (this.body.querySelector('.fe-reply textarea') as HTMLTextAreaElement | null)?.focus();
          });
          row.append(reply);
        }
        thread.append(row);
      }
      if ((ticket.status === 'resolved' || ticket.status === 'closed') && ticket.resolved_at) {
        const byUser = ticket.resolved_by === 'user';
        const label = ticket.status === 'closed' ? t.closedNote : byUser ? t.resolvedByYouNote : t.resolvedNote;
        thread.append(
          h('div', { class: 'fe-msg-row system', role: 'status' }, [
            h('div', { class: 'fe-msg system' }, [h('span', { class: 'fe-sys-ico', 'aria-hidden': 'true', html: icons.check }), label, h('span', { class: 'fe-meta' }, [`${byUser ? t.you : t.staff} · ${this.date(ticket.resolved_at)}`])]),
          ]),
        );
      }
      this.body.append(thread);
      const mentioned = ticket.role === 'mentioned';
      if (mentioned && ticket.status !== 'closed') this.body.append(h('p', { class: 'fe-muted fe-mention-note', role: 'status' }, [t.mentionedNote]));
      // While staff are on it, the reporter may close the thread themselves (a mentioned user may not).
      if (!mentioned && ['in_progress', 'queued', 'processing', 'waiting_user'].includes(ticket.status)) this.body.append(this.resolveCta(id));
      if (ticket.status !== 'closed') {
        const composer = this.composer(id);
        const open = (ticket.messages ?? []).reduce((n, m) => n + (m.questions?.items.filter((q) => q.state === 'open').length ?? 0), 0);
        if (open) composer.prepend(this.questionBar(open));
        this.body.append(composer);
      }
      this.body.scrollTop = this.body.scrollHeight;
      void this.api.listTickets({ per_page: 1 }).then((r) => this.setUnread(r.unread, r.mentions_unread ?? 0)).catch(() => undefined);
    } catch {
      this.body.innerHTML = '';
      this.body.append(h('p', { class: 'fe-center' }, [t.error]));
    }
  }

  /**
   * Question block ("Size soru soruldu"): a full-width card with an answer field under every open question and one
   * button that sends the filled ones. Answered questions show the answer; the card turns green once none is open.
   */
  private questionCard(id: number, ticket: Ticket, m: TicketMessage): HTMLElement {
    const t = this.t.q;
    const b = m.questions!;
    const done = b.total > 0 && b.answered >= b.total;
    const card = h('div', { class: `fe-msg fe-qa${done ? ' done' : ''}`, 'data-mid': String(m.id) }, [
      h('div', { class: 'fe-qa-tag', html: (done ? icons.check : icons.ask) + escape(done ? t.answered : t.asked + (b.total > 1 ? ` · ${fmt(t.progress, { a: b.answered, n: b.total })}` : '')) }),
    ]);
    if (b.intro) card.append(h('p', {}, [b.intro]));
    const fields: [number, HTMLTextAreaElement][] = [];
    const send = h('button', { class: 'fe-btn primary', type: 'button' }, [t.send]);
    const err = h('div', { class: 'fe-error', role: 'alert' });
    const sync = () => (send.disabled = !fields.some(([, f]) => f.value.trim()));
    const ol = h('ol');
    for (const q of b.items) {
      const li = h('li', { class: q.state === 'withdrawn' ? 'off' : undefined }, [h('div', { class: 'fe-qa-q' }, [`${q.position}. ${q.body}`])]);
      if (q.state === 'withdrawn') li.append(h('small', {}, [t.withdrawn]));
      else if (q.state === 'answered') li.append(h('div', { class: 'fe-qa-a' }, [q.answer ?? '', h('small', {}, [`${q.mine ? this.t.mine.you : q.answered_by ?? ''} · ${this.date(q.answered_at ?? m.created_at)}`])]));
      else if (ticket.status === 'closed') li.append(h('small', {}, [t.unanswered]));
      else {
        const f = h('textarea', { class: 'fe-textarea', rows: '2', maxlength: '5000', placeholder: t.placeholder, 'aria-label': q.body, 'data-q-open': String(q.id) });
        f.value = this.qaDrafts.get(q.id) ?? '';
        f.addEventListener('input', () => {
          this.qaDrafts.set(q.id, f.value);
          sync();
        });
        fields.push([q.id, f]);
        li.append(f);
      }
      ol.append(li);
    }
    card.append(ol);
    if (fields.length) {
      sync();
      send.addEventListener('click', async () => {
        const answers = fields.filter(([, f]) => f.value.trim()).map(([qid, f]) => ({ question_id: qid, body: f.value.trim() }));
        if (!answers.length) return;
        send.disabled = true;
        err.textContent = '';
        try {
          await this.api.answer(id, m.id, answers);
          for (const a of answers) this.qaDrafts.delete(a.question_id);
          await this.renderThread(id);
        } catch (e) {
          // 409: answered meanwhile or the ticket was closed — the fresh thread shows which.
          if (e instanceof ApiError && e.status === 409) return void this.renderThread(id);
          err.textContent = t.failed;
          sync();
        }
      });
      card.append(h('div', { class: 'fe-qa-foot' }, [err, send]));
    }
    card.append(h('span', { class: 'fe-meta' }, [`${this.t.mine.staff} · ${this.date(m.created_at)}${m.edited_at ? ` · ${this.t.mine.edited}` : ''}`]));
    return h('div', { class: 'fe-msg-row qa' }, [card]);
  }

  /** "Cevap bekleyen N soru var · Cevapla" above the reply box: jumps to the first open answer field. */
  private questionBar(n: number): HTMLElement {
    const bar = h('button', { class: 'fe-qa-bar', type: 'button', html: icons.ask + escape(fmt(this.t.q.pending, { n })) }, [h('strong', {}, [`${this.t.q.go} →`])]);
    bar.addEventListener('click', () => {
      const f = this.body.querySelector('[data-q-open]') as HTMLTextAreaElement | null;
      f?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      f?.focus({ preventScroll: true });
    });
    return bar;
  }

  /**
   * "Problem Çözüldü" panel under the thread: one centred button, then an inline confirm so a stray
   * tap cannot close the ticket. On success the thread is re-rendered as "Çözüldü".
   */
  private resolveCta(id: number): HTMLElement {
    const t = this.t.mine;
    const box = h('div', { class: 'fe-resolve' });
    const render = (confirming: boolean) => {
      box.innerHTML = '';
      if (!confirming) {
        const ask = h('button', { class: 'fe-btn primary', type: 'button', html: icons.check + escape(t.resolveCta) });
        ask.addEventListener('click', () => render(true));
        box.append(ask, h('p', { class: 'fe-resolve-hint' }, [t.resolveHint]));
        return;
      }
      const err = h('div', { class: 'fe-error' });
      const yes = h('button', { class: 'fe-btn primary', type: 'button' }, [t.resolveYes]);
      const no = h('button', { class: 'fe-btn', type: 'button' }, [t.resolveCancel]);
      no.addEventListener('click', () => render(false));
      yes.addEventListener('click', async () => {
        yes.setAttribute('disabled', '');
        no.setAttribute('disabled', '');
        err.textContent = '';
        try {
          await this.api.resolveTicket(id);
          await this.renderThread(id);
        } catch {
          err.textContent = t.resolveFailed;
          yes.removeAttribute('disabled');
          no.removeAttribute('disabled');
        }
      });
      box.append(h('p', { class: 'fe-resolve-hint' }, [t.resolveConfirm]), h('div', { class: 'fe-resolve-actions' }, [yes, no]), err);
      yes.focus();
    };
    render(false);
    return box;
  }

  /** Reply composer: "+" menu (screenshot / file), pending-attachment strip, draft kept per ticket. */
  private composer(id: number): HTMLElement {
    const t = this.t.mine;
    const wrap = h('div', { class: 'fe-reply-wrap' });
    const strip = h('div', { class: 'fe-reply-att', hidden: true });
    const quoteStrip = h('div', { class: 'fe-reply-quote', hidden: true });
    const renderQuote = () => {
      quoteStrip.innerHTML = '';
      const q = this.replyTo.get(id);
      quoteStrip.hidden = !q;
      if (!q) return;
      const cancel = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.form.remove, title: this.t.form.remove, html: icons.close });
      cancel.addEventListener('click', () => {
        this.replyTo.delete(id);
        renderQuote();
      });
      quoteStrip.append(h('span', { class: 'fe-quote-ico', 'aria-hidden': 'true', html: icons.reply }), h('div', { class: 'fe-quote-text' }, [h('strong', {}, [`${t.replyingTo} ${this.who(q)}`]), h('span', {}, [q.body])]), cancel);
    };
    this.onReplyToChange = renderQuote;
    const err = h('div', { class: 'fe-error' });
    const input = h('textarea', { class: 'fe-textarea', placeholder: t.reply, 'aria-label': t.reply, maxlength: '10000' });
    input.value = this.drafts.get(id) ?? '';
    input.addEventListener('input', () => this.drafts.set(id, input.value));
    const picker = this.state.identity?.hash ? this.mentionPicker(id, input) : null;
    const send = h('button', { class: 'fe-btn primary', type: 'button', 'aria-label': t.send, html: icons.send });
    const plus = h('button', { class: 'fe-btn fe-plus', type: 'button', 'aria-label': t.attach, 'aria-haspopup': 'menu', 'aria-expanded': 'false', html: icons.plus });
    const menu = h('div', { class: 'fe-menu', role: 'menu', hidden: true });
    const fileInput = h('input', { type: 'file', class: 'fe-sr', 'aria-label': t.sendFile });
    const slot = { get: () => this.replyAttachment, set: (a: Att | null) => this.setReplyAttachment(a) };
    const recSlot = h('div');
    const renderRec = () => {
      recSlot.innerHTML = '';
      if (this.rec?.target === id) recSlot.append(this.recRow(renderRec));
    };

    const renderStrip = () => {
      strip.innerHTML = '';
      const a = this.replyAttachment;
      strip.hidden = !a;
      if (!a) return;
      if (a.preview) strip.append(h('img', { src: a.preview, alt: a.name }));
      strip.append(h('span', { class: 'fe-att-name' }, [a.name, ' ', h('small', {}, [this.fileSize(a.blob.size)])]));
      if (a.preview && a.blob.type !== 'application/pdf') {
        const edit = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.form.edit, title: this.t.form.edit, html: icons.pen });
        edit.addEventListener('click', () => void this.openAnnotator(renderStrip, err, slot));
        strip.append(edit);
      }
      const rm = h('button', { class: 'fe-icon-btn', type: 'button', 'aria-label': this.t.form.remove, title: this.t.form.remove, html: icons.close });
      rm.addEventListener('click', () => {
        this.setReplyAttachment(null);
        renderStrip();
      });
      strip.append(rm);
    };

    const close = () => {
      menu.hidden = true;
      plus.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDoc, true);
      this.closeMenu = null;
    };
    const onDoc = (e: Event) => {
      if (!e.composedPath().includes(wrap)) close();
    };
    const open = () => {
      this.closeMenu?.();
      menu.hidden = false;
      plus.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onDoc, true);
      this.closeMenu = close;
      (menu.firstElementChild as HTMLElement | null)?.focus();
    };
    plus.addEventListener('click', () => (menu.hidden ? open() : close()));
    menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close();
        plus.focus();
      }
    });

    const item = (label: string, icon: string, onPick: () => void) => {
      const b = h('button', { class: 'fe-menu-item', type: 'button', role: 'menuitem', html: icon + escape(label) });
      b.addEventListener('click', () => {
        close();
        onPick();
      });
      return b;
    };
    menu.append(
      item(t.sendShot, icons.camera, async () => {
        err.textContent = '';
        try {
          const shot = await this.captureFlow();
          if (!shot) return;
          this.setReplyAttachment(shot);
          renderStrip();
          await this.openAnnotator(renderStrip, err, slot);
        } catch {
          err.textContent = this.t.form.captureFailed;
        }
        input.focus();
      }),
      item(t.sendFile, icons.clip, () => this.pick(fileInput)),
      ...(this.recOn() ? [item(this.t.form.rec.start, icons.rec, () => void this.startRec({ target: id, draft: { body: input.value } }, err))] : []),
    );
    fileInput.addEventListener('change', () => {
      err.textContent = '';
      const f = fileInput.files?.[0];
      fileInput.value = '';
      if (!f) return;
      const att = this.fileToAttachment(f, err);
      if (att) {
        this.setReplyAttachment(att);
        renderStrip();
      }
      input.focus();
    });
    // paste an image straight into the reply box; drop a file anywhere on the thread
    input.addEventListener('paste', (e) => {
      const f = Array.from(e.clipboardData?.files ?? [])[0];
      if (!f) return;
      e.preventDefault();
      err.textContent = '';
      const att = this.fileToAttachment(f, err);
      if (att) {
        this.setReplyAttachment(att);
        renderStrip();
      }
    });
    this.body.addEventListener('dragover', (e) => {
      if (e.dataTransfer?.types.includes('Files')) {
        e.preventDefault();
        this.body.classList.add('fe-drop');
      }
    });
    this.body.addEventListener('dragleave', () => this.body.classList.remove('fe-drop'));
    this.body.addEventListener('drop', (e) => {
      this.body.classList.remove('fe-drop');
      const f = e.dataTransfer?.files?.[0];
      if (!f) return;
      e.preventDefault();
      err.textContent = '';
      const att = this.fileToAttachment(f, err);
      if (att) {
        this.setReplyAttachment(att);
        renderStrip();
      }
    });

    send.addEventListener('click', async () => {
      const body = input.value.trim();
      const att = this.replyAttachment;
      const rec = this.rec?.target === id ? this.rec : null;
      if (!body && !att && !rec) return;
      send.disabled = true;
      plus.disabled = true;
      input.disabled = true;
      err.textContent = '';
      try {
        const tokens: string[] = [];
        if (att) {
          strip.append(h('span', { class: 'fe-att-progress' }, [t.uploading]));
          tokens.push(await this.uploadAttachment(att));
        }
        if (rec) tokens.push(await this.api.upload(rec.blob, 'ekran-kaydi.json.gz', 'replay'));
        const picks = [...(this.mentionPicks.get(id) ?? new Map<number, string>())];
        await this.api.reply(id, body, tokens, this.replyTo.get(id)?.id, picks.filter(([, name]) => body.includes('@' + name)).map(([uid]) => uid));
        this.drafts.delete(id);
        this.replyTo.delete(id);
        this.mentionPicks.delete(id);
        this.setReplyAttachment(null);
        if (rec) this.dropRec();
        await this.renderThread(id);
      } catch (e) {
        // 409 = the ticket was closed meanwhile: re-render so the composer disappears and the closed note shows.
        if (e instanceof ApiError && e.status === 409) {
          await this.renderThread(id);
          return;
        }
        err.textContent = t.sendFailed;
        send.disabled = false;
        plus.disabled = false;
        input.disabled = false;
        renderStrip();
      }
    });
    input.addEventListener('keydown', (e) => {
      if (e.defaultPrevented) return; // the "@" picker took the key
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') send.click();
    });

    renderStrip();
    renderQuote();
    renderRec();
    wrap.append(quoteStrip, strip, recSlot, err, ...(picker ? [picker] : []), h('div', { class: 'fe-reply' }, [h('div', { class: 'fe-plus-wrap' }, [plus, menu, fileInput]), input, send]));
    return wrap;
  }

  /**
   * "@" autocomplete over the reply box: typing "@me" lists matching people of the project (names only),
   * ↑/↓ + Enter/Tab or a click inserts "@Ad Soyad " and remembers the id for the send.
   */
  private mentionPicker(id: number, input: HTMLTextAreaElement): HTMLElement {
    const pop = h('div', { class: 'fe-mention-pop', role: 'listbox', 'aria-label': this.t.mine.mentionPick, hidden: true });
    let users: Mention[] = [];
    let active = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let seq = 0;
    const hide = () => {
      pop.hidden = true;
      users = [];
    };
    const query = () => MENTION_AT.exec(input.value.slice(0, input.selectionStart ?? input.value.length))?.[1] ?? null;
    const pick = (u: Mention) => {
      const caret = input.selectionStart ?? input.value.length;
      const before = input.value.slice(0, caret).replace(/@[^@]*$/u, '@' + u.name + ' ');
      input.value = before + input.value.slice(caret);
      input.setSelectionRange(before.length, before.length);
      this.drafts.set(id, input.value);
      const picks = this.mentionPicks.get(id) ?? new Map<number, string>();
      picks.set(u.id, u.name);
      this.mentionPicks.set(id, picks);
      hide();
      input.focus();
    };
    const paint = () => {
      pop.replaceChildren(
        ...users.map((u, i) => {
          const b = h('button', { class: 'fe-mention-opt', type: 'button', role: 'option', 'aria-selected': i === active ? 'true' : 'false' }, [h('span', { class: 'fe-avatar', 'aria-hidden': 'true' }, [u.name.slice(0, 1).toUpperCase()]), u.name]);
          // mousedown keeps the textarea focused (and its caret) while picking
          b.addEventListener('mousedown', (e) => {
            e.preventDefault();
            pick(u);
          });
          return b;
        }),
      );
      pop.hidden = users.length === 0;
    };
    input.addEventListener('input', () => {
      if (timer) clearTimeout(timer);
      const q = query();
      if (q === null) return hide();
      timer = setTimeout(() => {
        const mySeq = ++seq;
        this.api
          .mentionUsers(q, id)
          .then((r) => {
            if (mySeq !== seq || query() !== q) return;
            users = r.users;
            active = 0;
            paint();
          })
          .catch(hide);
      }, 180);
    });
    input.addEventListener('blur', () => setTimeout(hide, 150));
    input.addEventListener('keydown', (e) => {
      if (pop.hidden || !users.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        active = (active + (e.key === 'ArrowDown' ? 1 : users.length - 1)) % users.length;
        paint();
      } else if (e.key === 'Enter' || e.key === 'Tab') pick(users[active]!);
      else if (e.key === 'Escape') {
        hide();
        e.stopPropagation(); // keep the panel open
      } else return;
      e.preventDefault();
    });
    return pop;
  }

  /* ---------- tab 3: status ---------- */

  private async renderStatus(): Promise<void> {
    const t = this.t.status;
    this.body.innerHTML = '';
    this.body.append(h('p', { class: 'fe-center' }, [t.loading]));
    try {
      const { announcements } = await this.api.status();
      if (this.tab !== 'status') return;
      this.state.setAnnouncements(announcements);
      this.renderAnnouncements(announcements);
    } catch {
      this.renderAnnouncements(this.state.remote.announcements ?? []);
    }
  }

  private renderAnnouncements(list: Announcement[]): void {
    const t = this.t.status;
    this.body.innerHTML = '';
    // Viewing the tab counts as reading: clear the "Durum" badge and, if nothing else is unread, the launcher glow.
    this.state.markAnnouncementsSeen(list.map((a) => a.id));
    this.refreshAttention();
    if (!list.length) {
      this.body.append(h('div', { class: 'fe-center' }, [h('div', { class: 'fe-big', html: icons.check }), h('p', { style: 'margin:0' }, [t.ok])]));
      return;
    }
    for (const a of list) {
      this.body.append(h('div', { class: `fe-ann ${a.level}` }, [h('div', { class: 'fe-meta' }, [t.levels[a.level] ?? a.level]), h('div', {}, [a.message])]));
    }
  }

  /** "between X and Y" / "by Y" — null when no window is planned. */
  private plannedText(ticket: Ticket): string | null {
    if (!ticket.planned_until) return null;
    const t = this.t.mine;
    return ticket.planned_from ? fmt(t.plannedBetween, { from: this.date(ticket.planned_from), until: this.date(ticket.planned_until) }) : fmt(t.plannedBy, { until: this.date(ticket.planned_until) });
  }

  private date(iso: string): string {
    try {
      return new Date(iso).toLocaleString(this.state.options.locale === 'en' ? 'en-GB' : 'tr-TR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  }
}
